/******************************************************************************
 * 
 * Strauss Motion
 * 
 * Copyright (c) 2022 Robimo GmbH
 *
 * Authors: Daniel Martinek
 *
 *****************************************************************************/
import { Container} from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { UIState, ConfigState, LibraryData, ProductState } from '../store/interfaces';

import styles from '../styles/orderscreen.module.scss'
import { LogoText } from './LogoText';

import {ReactComponent as IconArrowLeft} from '../icons/arrow_left.svg';
import {ReactComponent as IconArrowRight} from '../icons/arrow_right.svg';
import {ReactComponent as IconPersonalize} from '../icons/personalize.svg';
import {ReactComponent as IconPhone} from '../icons/phone.svg';
import {ReactComponent as IconCheckmark} from '../icons/checkmark.svg';
import {ReactComponent as IconShirt} from '../icons/logo.svg';


import { getFinalPricingNoVat, getPricingTextLong, getShippingPrice, addPrice, applyVat, getVat } from '../helpers/pricing';
import { Fragment, useRef } from 'react';
import { Progress } from './Progress';
import CurrentConfigContainer from '../containers/CurrentConfigContainer';
import AdditionalProductsContainer from '../containers/AdditionalProductsContainer';
import LanguageSwitcherContainer from '../containers/LanguageSwitcherContainer';
import { Product } from './Product';
import { isMobile } from 'react-device-detect';
import { PreviewContainer, PreviewContainerModel } from './Preview';
import LinkFooterContainer from '../containers/LinkFooterContainer';
import { getMinimumOrderAmount, getTotalProductOrderAmountForMinimumOrder } from '../helpers/helpers';

 interface OrderscreenProps {
    ui: UIState;
    config: ConfigState;
    library: LibraryData;
    eventRef?: React.MutableRefObject<HTMLDivElement>;
    scrollRef?: React.MutableRefObject<HTMLDivElement>;
    onUiGoTo: (route: string) => void;
    onUiSetMobileOrderStep: (step: string) => void;
    onUiSetDetailArticleIndex: (detailArticleIndex: number) => void;
    onUiSetEditScreenTab: (editScreenTab: string) => void;
    onConfigSetBasics: (basics: boolean) => void;
    onConfigSetInspectColor: (inspectColor: boolean) => void;
    onConfigSetInspectSize: (inspectSize: boolean) => void;
    onProductUpdate: (index: number, state: ProductState) => void;
    onProductRemove: (index: number) => void;
    onUiSetPreviewArticle1: (article: string) => void;
}

export function Orderscreen(props: OrderscreenProps) {
    const {t, i18n} = useTranslation();

    const eventRef = useRef<HTMLDivElement>(null!);
    const scrollRef = useRef<HTMLDivElement>(null!);
    const canvasRef = useRef<HTMLCanvasElement>(null!);

    const divRefs = [
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!)
    ];

    let totalAmountNoVat = getFinalPricingNoVat(props.library, props.config.products);
    let shippingPrice = getShippingPrice(props.library, totalAmountNoVat);

    let allowedToContinue = props.config.products.length > 0 && props.config.products.every((product) => {
        let article = props.library.products.find((article) => {
            return article.article === product.article;
        });
        if(article === undefined) {
            return false;
        }
        let overallAmount = getTotalProductOrderAmountForMinimumOrder(article, props.config, props.library);
        return overallAmount >= getMinimumOrderAmount(article, props.library);
    });
    
    let totalVat = getVat(totalAmountNoVat, props.ui.country.vat);
    let totalAmountVat = addPrice(applyVat(totalAmountNoVat, props.ui.country.vat), shippingPrice);

    const isEmpty = (props.config.products.length === 0);
    
    let firstPersonalizedArticleIndex = props.config.products.findIndex((product) => {
        return product.logos.length > 0;
    });

    // Note(daniel): Needed to correctly align canvas views after the sidebar animation.
    setTimeout(() => {
        if(canvasRef.current) {
            window.dispatchEvent(new Event('resize'));
            canvasRef.current.parentElement?.parentElement?.classList.add(styles.visible);
        }
    }, 1100);

    return (
        <Container fluid className={styles.container} ref={scrollRef}>  
            <div className={styles.content} ref={eventRef}>  
                <PreviewContainer
                    className={styles.fullCanvas}
                    eventRef={props.eventRef !== undefined ? props.eventRef : eventRef}
                    scrollRef={props.scrollRef !== undefined ? props.scrollRef : scrollRef}
                    canvasRef={canvasRef}
                >
                    {
                        props.config.products.map((product, index) => {
                            let article = props.library.products.find((article) => {
                                return article.article === product.article;
                            });
                            if(article === null || article === undefined) {
                                return null;
                            }
                            return <PreviewContainerModel key={index} divRef={divRefs[index]} library={props.library} model={article.article} thumbnail={true} modelData={article.modelDataThumbnail !== undefined ? article.modelDataThumbnail : article.modelData} xOffset={0} baseColorId={props.config.baseColorId} contrastColorId={props.config.contrastColorId} straussLogo={props.config.straussLogo} showPlanes={false}/>
                        })
                    }
                </PreviewContainer>
                <div className={styles.header}>
                    <LogoText size={'small'} black={true}/>
                </div>
                <div className={styles.products}>
                    <div className={styles.productsLeft}>
                        <div className={styles.productsHeader}>
                            <button className={`${styles.buttonWhite}`} onClick={(evt) => {props.onUiGoTo('editscreen')}}><IconArrowLeft/>{t('orderscreen.backToOverview')}</button>
                            <Progress progress={0}/>
                        </div>
                        {isEmpty && 
                            <Fragment>
                                <h1>{t('orderscreen.headerEmpty')}</h1>
                                <p>{t('orderscreen.subHeaderEmpty')}</p>
                                <div className={styles.emptyButtons}>
                                    <button className={`${styles.buttonBlack}`} onClick={() => {props.onUiGoTo('inspiration')}}>{t('orderscreen.getInspired')}</button>
                                    <button className={`${styles.buttonPrimary}`} onClick={() => {props.onUiGoTo('editscreen'); props.onUiSetEditScreenTab('products');}}>{t('orderscreen.addProducts')}</button>
                                </div>
                            </Fragment>
                        }
                        {!isEmpty && 
                            <Fragment>
                                <h1>{t('orderscreen.header')}</h1>
                                <p>{t('orderscreen.subHeader')}</p>
                                {
                                    props.config.products.map((product, index) => {
                                        let article = props.library.products.find((article) => {
                                            return article.article === product.article;
                                        });
                                        if(article === null || article === undefined) {
                                            return null;
                                        }
                                        return <Fragment key={index}>
                                            {index === firstPersonalizedArticleIndex && <div className="personalizedInfoContainer">
                                                <div className="personalizedInfoWhite">
                                                    <IconPersonalize/>
                                                </div>
                                                <div className="personalizedInfoConsultingHeader">
                                                    <h1 dangerouslySetInnerHTML={{__html: t('orderscreen.personalized.consultingHeader')}}></h1>
                                                </div>
                                                <div className="personalizedInfoCallHeader">
                                                    <h1>{t('orderscreen.personalized.callHeader')}</h1>
                                                    <p>{t('orderscreen.personalized.callText')}</p>
                                                </div>
                                                <div className="personalizedInfoGreen">
                                                    <IconPhone/>
                                                </div>
                                            </div>}
                                            <Product
                                                library={props.library}
                                                config={props.config}
                                                country={props.ui.country}
                                                product={product}
                                                article={article}
                                                index={index}
                                                key={index}
                                                vat={props.ui.country.vat}
                                                noSeparationLines={isMobile}
                                                divRef={divRefs[index]}
                                                onProductUpdate={props.onProductUpdate}
                                                onProductRemove={props.onProductRemove}
                                                onUiGoTo={props.onUiGoTo}
                                                onUiSetPreviewArticle1={props.onUiSetPreviewArticle1}
                                                onUiSetEditScreenTab={props.onUiSetEditScreenTab}
                                                onUiSetDetailArticleIndex={props.onUiSetDetailArticleIndex}
                                            />
                                        </Fragment>
                                    })
                                }
                                <div className={styles.productSumContainer}>
                                    <div className={styles.productSum}>
                                        <div className={`${styles.productColumns} ${styles.borderBottom}`}>
                                            <div className={styles.col0}>
                                                <span className={styles.sumBold}>{t('orderscreen.sum.totalNoVat')}</span>
                                            </div>
                                            <div className={styles.col1}>
                                                <span className={styles.sumBold}>{getPricingTextLong(i18n.language, props.ui.country, totalAmountNoVat)}</span>
                                            </div>
                                            <div className={styles.col0}>
                                                <span>{t('orderscreen.sum.shipping')}</span>
                                            </div>
                                            <div className={styles.col1}>
                                                <span>{t('orderscreen.sum.free')}</span>
                                            </div>
                                            <div className={styles.col0}>
                                                <span>{t('orderscreen.sum.additionalVat')}</span>
                                            </div>
                                            <div className={styles.col1}>
                                                <span>{getPricingTextLong(i18n.language, props.ui.country, totalVat)}</span>
                                            </div>
                                        </div>
                                        <div className={styles.productColumns}>
                                            <div className={styles.col0}>
                                                <span className={styles.sumTotal}>{t('orderscreen.sum.totalWithVat')}</span>
                                            </div>
                                            <div className={styles.col1}>
                                                <span className={styles.sumTotalBold}>{getPricingTextLong(i18n.language, props.ui.country, totalAmountVat)}</span>
                                            </div>
                                        </div>
                                        <span>{t('orderscreen.sum.withVat')}</span>
                                    </div>
                                </div>
                                <div className={styles.basicsContainer} data-testid="mymotion_orderListCheckColour" onClick={() => {
                                    props.onConfigSetInspectColor(!props.config.inspectColor);
                                }}>
                                    <div className={`${styles.basicsSelector} ${props.config.inspectColor ? styles.basicsSelectorActive : ''}`}>
                                        {props.config.inspectColor && 
                                            <IconCheckmark/>
                                        }
                                    </div>
                                    <div className={`${styles.imageContainer} ${styles.colorImage}`}>
                                    </div>
                                    <div>
                                        <h1>{t('orderscreen.inspectColors.header')}</h1>
                                        <p>{t('orderscreen.inspectColors.text')}</p>
                                    </div>
                                </div>
                                <div className={styles.basicsContainer} data-testid="mymotion_orderListCheckSize" onClick={() => {
                                    props.onConfigSetInspectSize(!props.config.inspectSize);
                                }}>
                                    <div className={`${styles.basicsSelector} ${props.config.inspectSize ? styles.basicsSelectorActive : ''}`}>
                                        {props.config.inspectSize && 
                                            <IconCheckmark/>
                                        }
                                    </div>
                                    <div className={`${styles.imageContainer} ${styles.measureImage}`}>
                                        
                                    </div>
                                    <div>
                                        <h1>{t('orderscreen.inspectSize.header')}</h1>
                                        <p>{t('orderscreen.inspectSize.text')}</p>
                                    </div>
                                </div>
                                <div className={styles.basicsContainer} data-testid="mymotion_orderListCheckBasics" onClick={() => {
                                    props.onConfigSetBasics(!props.config.addBasics);
                                }}>
                                    <div className={`${styles.basicsSelector} ${props.config.addBasics ? styles.basicsSelectorActive : ''}`}>
                                        {props.config.addBasics && 
                                            <IconCheckmark/>
                                        }
                                    </div>
                                    <div className={styles.imageContainer}>
                                        <IconShirt/>
                                    </div>
                                    <div>
                                        <h1>{t('orderscreen.basics.header')}</h1>
                                        <p>{t('orderscreen.basics.text')}</p>
                                    </div>
                                </div>
                                <div className={styles.fill}></div>
                                {!isMobile && 
                                    <div className={styles.footerButtons}>
                                        <button className={`${styles.buttonWhite}`} onClick={(evt) => {props.onUiGoTo('editscreen')}}><IconArrowLeft/>{t('orderscreen.backToOverview')}</button>
                                        <button data-testid="mymotion_orderListNextStep" className={`${styles.buttonPrimary}`} disabled={!allowedToContinue} onClick={(evt) => {props.onUiGoTo('customerscreen')}}>{t('orderscreen.nextStep')}<IconArrowRight/></button>
                                    </div>     
                                }
                            </Fragment>  
                        }  
                    </div>
                    <div className={styles.productsRight}>
                        {(!isMobile) && 
                            <CurrentConfigContainer/>
                        }
                        {(!isEmpty || isMobile) && 
                            <AdditionalProductsContainer eventRef={props.eventRef !== undefined ? props.eventRef : eventRef} scrollRef={props.scrollRef !== undefined ? props.scrollRef : scrollRef}/>
                        }
                    </div>
                    
                    {isMobile && 
                        <div className={styles.footerButtons}>
                            <button className={`${styles.buttonWhite}`} onClick={(evt) => {props.onUiGoTo('editscreen'); props.onUiSetEditScreenTab('');}}><IconArrowLeft/>{t('misc.back')}</button>
                            <button data-testid="mymotion_orderListNextStep" className={`${styles.buttonPrimary}`} disabled={!allowedToContinue} onClick={(evt) => {
                                if(isMobile){ 
                                    props.onUiSetMobileOrderStep('customer'); 
                                    props.onUiGoTo('editscreen')}
                                else {
                                    props.onUiGoTo('customerscreen')
                                }
                            }}>{t('orderscreen.nextStep')}<IconArrowRight/></button>
                        </div>
                    }
                </div>
                <div className={styles.footer}><LanguageSwitcherContainer black/><span className={styles.footerSeparator}></span><LinkFooterContainer fullWidth={true}/></div>
            </div>
        </Container>
    );
};